import styled, { css } from 'styled-components';
import { ProductDTO } from 'src/models/pim';
import {
  ProductAttributeCode,
  ProductAttributeCollection,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { Heading } from 'src/components/common/Heading';
import { Paragraph } from 'src/components/common/typography/Text';
import { useMemo } from 'react';
import { ProductPageHowItWorksItem } from 'src/modules/products/components/ProductPage/ProductPageHowItWorks/ProductPageHowItWorksItem';
import { media } from 'src/styles/designs';
import { head } from 'lodash';
import { Theme } from 'vendor/mods/styled-components';
import { redTheme } from 'src/styles/theme';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';

const ContentContainer = styled.div`
  display: grid;
  gap: 48px;

  ${media.w.greaterThanOrEqual.desktop} {
    gap: 110px;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Container = styled.section<{ $singular?: boolean }>`
  padding: 32px 0;
  background: ${({ theme }) => theme.palette.background};

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 56px 0;

    ${casePathEmptyString('$singular')(css`
      ${ConstraintContainer} {
        margin-top: 16px;
      }
      ${ContentContainer} {
        grid-template-columns: 1fr;
      }
    `)}
  }
`;

export const ProductPageHowItWorks = (product: ProductDTO) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute, getMultipleProductAttributes } =
    useProductAttributes(product.attributes);

  const [title] = useTitlesDictionary(DictionaryTitlesCode.HowItWorks);

  const subtitle = head(
    getProductAttribute(ProductAttributeCode.HowItWorksSubtitle),
  );

  const items = useMemo(
    () =>
      getMultipleProductAttributes(ProductAttributeCode.HowItWorksGroup).map(
        (attributes) => ({
          title: head(
            getProductAttribute(
              ProductAttributeCode.HowItWorksGroupTitle,
              attributes,
            ),
          ),
          description: head(
            getProductAttribute(
              ProductAttributeCode.HowItWorksGroupText,
              attributes,
            ),
          ),
          image: head(
            getProductAttribute(
              ProductAttributeCollection.ImagesValues,
              attributes,
            ),
          ),
        }),
      ),
    [getMultipleProductAttributes, getProductAttribute],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Theme theme={redTheme}>
      <Container $singular={items.length === 1}>
        <ConstraintContainer>
          <Heading>
            {title && <Markdown>{title.value}</Markdown>}
            {subtitle && <Paragraph>{translateFn(subtitle).value}</Paragraph>}
          </Heading>

          <ContentContainer>
            {items.map((item, i) => (
              <ProductPageHowItWorksItem
                key={i}
                singular={items.length === 1}
                {...item}
              />
            ))}
          </ContentContainer>
        </ConstraintContainer>
      </Container>
    </Theme>
  );
};
